import { motion } from 'framer-motion';
import {
  filterHeroKpiTiles,
  resolveHeroKpiSubcopy,
  type HeroKpiTile,
  buildHeroTagline,
} from '@act/shared/data-transfer-objects';
import AnimatedCounter from './AnimatedCounter';
import logoCoral from '../assets/logo-coral.svg';
import { ReportCallout } from '../ReportCallout';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import {
  kpiTileSuffix,
  parseKpiTileNumber,
  type ReportHostedSectionProps,
} from '../reportHostedSection.types';
import { heroKpiGridClassName } from './heroKpiGrid.util';

const midnight = 'var(--reema-midnight-100)';
const coral = 'var(--reema-coral-100)';

const readKpiTiles = (data: Record<string, unknown>): HeroKpiTile[] =>
  Array.isArray(data.kpiTiles) ? (data.kpiTiles as HeroKpiTile[]) : [];

/**
 * Full-bleed hero cover (Studio parity) driven by hero snapshot KPI tiles.
 * @param props - Resolved snapshot, customer context, and optional callout.
 */
const HeroSection = ({
  data,
  customer,
  reportingPeriod,
  measurementPeriod,
  callout = null,
  calloutTitle = null,
  calloutEnabled = false,
  heroKpiVisibility,
  authoringEditable = false,
  onCalloutChange,
  onCalloutTitleChange,
}: ReportHostedSectionProps) => {
  const printStatic = useReportsPrintStaticLayout();

  const eyebrow = buildHeroTagline(
    customer.name,
    measurementPeriod ?? reportingPeriod,
  ).toUpperCase();

  const rawHeadline =
    typeof data.headline === 'string' && data.headline.length > 0
      ? data.headline
      : 'Your hardest-to-reach members are getting the help they need.';
  const headline = rawHeadline.endsWith('.') ? rawHeadline : `${rawHeadline}.`;

  const stats = filterHeroKpiTiles(readKpiTiles(data), heroKpiVisibility).map(
    tile => ({
      label: tile.label,
      value: parseKpiTileNumber(tile.value),
      suffix: kpiTileSuffix(tile.value),
      decimals:
        kpiTileSuffix(tile.value) === '/5' &&
        !Number.isInteger(parseKpiTileNumber(tile.value))
          ? 1
          : 0,
      sub: resolveHeroKpiSubcopy(tile, customer.name),
    }),
  );

  const showCallout =
    calloutEnabled &&
    (authoringEditable || (callout !== null && callout.trim().length > 0));

  const statGrid =
    stats.length === 0 ? null : (
    <div
      className={heroKpiGridClassName(stats.length)}
      data-hero-kpi-count={stats.length}
    >
      {stats.map(stat => (
        <div
          key={stat.label}
          className="flex flex-col items-center text-center px-4 md:px-6 pt-5 md:pt-0"
        >
          <span
            className="text-[10px] font-bold uppercase tracking-[0.15em] leading-none mb-3"
            style={{ color: coral }}
          >
            {stat.label}
          </span>
          <div
            className="text-5xl lg:text-6xl font-medium tabular-nums mb-2 leading-none whitespace-nowrap"
            style={{
              color: '#fff',
              fontFamily: "'Lora', serif",
              letterSpacing: '-0.01em',
            }}
          >
            <AnimatedCounter
              end={stat.value}
              suffix={stat.suffix}
              decimals={'decimals' in stat ? stat.decimals : 0}
              duration={2}
            />
          </div>
          <span
            className="text-sm font-medium leading-snug"
            style={{ color: 'rgba(255,255,255,0.55)' }}
          >
            {stat.sub}
          </span>
        </div>
      ))}
    </div>
    );

  const logoClassName = 'h-8 mx-auto mb-4';
  const eyebrowClassName =
    'uppercase tracking-[0.25em] text-sm font-medium mb-2';
  const eyebrowStyle = {
    color: 'rgba(255,255,255,0.6)',
    fontFamily: 'var(--reema-font-body)',
  } as const;
  const headlineClassName =
    'text-4xl md:text-6xl lg:text-7xl leading-[1.05] mb-4';
  const headlineStyle = {
    color: '#fff',
    fontFamily: 'var(--reema-font-display)',
    fontWeight: 600,
    letterSpacing: '-0.01em',
  } as const;

  return (
    <section
      id="hero"
      className="relative overflow-hidden"
      style={{ background: midnight, color: '#fff' }}
    >
      <div className="relative z-10 flex flex-col items-center text-center px-6 py-[250px] w-full">
        <div className="w-full max-w-4xl">
          {printStatic ? (
            <>
              <img
                src={logoCoral}
                alt="Reema Health"
                className={logoClassName}
              />
              <p className={eyebrowClassName} style={eyebrowStyle}>
                {eyebrow}
              </p>
              <h1 className={headlineClassName} style={headlineStyle}>
                {headline}
              </h1>
              {statGrid}
            </>
          ) : (
            <>
              <motion.img
                src={logoCoral}
                alt="Reema Health"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={logoClassName}
              />
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={eyebrowClassName}
                style={eyebrowStyle}
              >
                {eyebrow}
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className={headlineClassName}
                style={headlineStyle}
              >
                {headline}
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                {statGrid}
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2 }}
                className="hero-scroll-hint mt-6"
              >
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-sm"
                  style={{ color: 'rgba(255,255,255,0.3)' }}
                >
                  Scroll to explore ↓
                </motion.div>
              </motion.div>
            </>
          )}
        </div>

        {showCallout ? (
          <div className="w-full max-w-3xl mt-6">
            <ReportCallout
              value={callout}
              title={calloutTitle}
              editable={authoringEditable}
              onChange={onCalloutChange}
              onTitleChange={onCalloutTitleChange}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default HeroSection;
